/**
 * CombatLogHUD - 실시간 종합 멀티채널 채팅 및 전투창 (DAoC 스타일 분리 창)
 * - 종합: 모든 채널의 대화 통합
 * - 일반: 근거리 (일정 거리 안) 흰색
 * - 파티: 파티원 간 (주황)
 * - 길드: 길드원 간 (초록)
 * - 방송: 현재 존 전체 방송 (진한하늘색)
 * - 월드: 전 월드 공지 및 서버점검 (초록)
 * - 귓속말: 1:1 귓속말 (핑크)
 * - 전투창: 탭 분리 독립 창 (채팅창 위에 배치, 흰색)
 */

import { LogMessage } from '../../core/Types';
import { EventBus } from '../../core/EventBus';

export type ChatChannelId = 'aggregate' | 'normal' | 'party' | 'guild' | 'broadcast' | 'world';

export class CombatLogHUD {
  private messages: LogMessage[] = [];
  private readonly maxMessages = 200;

  // 메인 채팅창 요소
  private chatEl: HTMLElement | null = null;
  private chatHeaderEl: HTMLElement | null = null;
  private chatBodyEl: HTMLElement | null = null;
  private chatChannelTagEl: HTMLElement | null = null;
  private chatInputEl: HTMLInputElement | null = null;
  private currentChatTab: ChatChannelId = 'aggregate';

  // 독립 전투창 요소
  private combatEl: HTMLElement | null = null;
  private combatBodyEl: HTMLElement | null = null;

  private sendHandler?: (
    channel: 'normal' | 'party' | 'guild' | 'broadcast' | 'world' | 'whisper',
    text: string,
    to?: string
  ) => void;

  private static readonly COMBAT_CATS = new Set(['combat', 'heal', 'threat', 'interrupted']);

  constructor() {
    EventBus.on('LOG_EMITTED', (msg) => {
      this.addMessage(msg);
    });
  }

  public setSendHandler(
    handler: (
      channel: 'normal' | 'party' | 'guild' | 'broadcast' | 'world' | 'whisper',
      text: string,
      to?: string
    ) => void
  ): void {
    this.sendHandler = handler;
  }

  public mount(parent: HTMLElement): void {
    // 1. 메인 채팅창 (#combat-log-hud)
    const chat = document.createElement('div');
    chat.id = 'combat-log-hud';
    chat.style.setProperty('display', 'flex', 'important');
    chat.style.flexDirection = 'column';
    chat.style.height = '100%';
    chat.style.boxSizing = 'border-box';
    chat.style.padding = '4px 8px 6px';
    chat.style.overflow = 'hidden';
    chat.style.fontFamily = 'monospace, sans-serif';
    chat.style.fontSize = '12px';
    chat.style.color = '#c9d1d9';
    chat.style.pointerEvents = 'auto';

    const header = document.createElement('div');
    header.style.cssText =
      'display:flex;flex-shrink:0;border-bottom:1px solid #000;margin:0 -8px 4px;padding-left:8px;background:#3a2a12;overflow-x:auto;white-space:nowrap;scrollbar-width:none;';

    const tabs: Array<{ id: ChatChannelId; label: string }> = [
      { id: 'aggregate', label: '종합' },
      { id: 'normal', label: '일반' },
      { id: 'party', label: '파티' },
      { id: 'guild', label: '길드' },
      { id: 'broadcast', label: '방송' },
      { id: 'world', label: '월드' },
    ];

    tabs.forEach((tb) => {
      const t = document.createElement('b');
      t.dataset.tab = tb.id;
      t.textContent = tb.label;
      t.style.cssText = 'font-size:10px;padding:2px 8px;border-right:1px solid #000;cursor:pointer;flex-shrink:0;';
      t.addEventListener('click', () => {
        this.currentChatTab = tb.id;
        this.updateChatTabs();
        this.updateChatChannelTag();
        this.renderChat();
      });
      header.appendChild(t);
    });
    this.chatHeaderEl = header;
    chat.appendChild(header);

    // 채팅 본문 메시지 영역 (flex:1로 확장되어 입력창 위 공간 차지)
    const chatBody = document.createElement('div');
    chatBody.id = 'combat-log-body';
    chatBody.style.cssText = 'flex:1 1 auto;min-height:0;overflow-y:auto;overflow-x:hidden;margin-bottom:4px;';
    chat.appendChild(chatBody);

    // Chat Input Bar - 창 맨 밑에 flex-shrink:0으로 확실히 하단 고정
    const inputRow = document.createElement('div');
    inputRow.id = 'chat-input-row';
    inputRow.style.cssText =
      'display:flex;align-items:center;gap:4px;flex-shrink:0;margin-top:auto;border-top:1px solid #2d2417;padding-top:4px;background:inherit;';

    const channelTag = document.createElement('span');
    channelTag.id = 'chat-channel-tag';
    channelTag.style.cssText =
      'font-size:10px;color:#f3d98b;background:#231a0e;padding:2px 5px;border-radius:3px;border:1px solid #4a381e;flex-shrink:0;';
    channelTag.textContent = '[일반]';

    const input = document.createElement('input');
    input.type = 'text';
    input.placeholder = 'Enter로 대화 (/p 파티, /g 길드, /b 방송, /w 상대 귓말)';
    input.style.cssText =
      'flex:1;background:#15100a;color:#f3f4f6;border:1px solid #4a381e;padding:3px 6px;font-size:11px;border-radius:3px;outline:none;font-family:inherit;';
    input.maxLength = 140;

    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        const val = input.value.trim();
        if (val) {
          this.submitChat(val);
          input.value = '';
        }
        input.blur();
      } else if (e.key === 'Escape') {
        input.blur();
      }
    });

    window.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && document.activeElement !== input) {
        const tag = document.activeElement?.tagName.toLowerCase();
        if (tag !== 'input' && tag !== 'textarea') {
          e.preventDefault();
          input.focus();
        }
      }
    });

    inputRow.appendChild(channelTag);
    inputRow.appendChild(input);
    chat.appendChild(inputRow);

    parent.appendChild(chat);
    this.chatEl = chat;
    this.chatBodyEl = chatBody;
    this.chatChannelTagEl = channelTag;
    this.chatInputEl = input;

    // 2. 분리된 독립 전투창 (#combat-standalone-hud)
    const combat = document.createElement('div');
    combat.id = 'combat-standalone-hud';
    combat.style.display = 'flex';
    combat.style.flexDirection = 'column';
    combat.style.padding = '4px 8px 6px';
    combat.style.overflow = 'hidden';
    combat.style.fontFamily = 'monospace, sans-serif';
    combat.style.fontSize = '12px';
    combat.style.color = '#ffffff';
    combat.style.pointerEvents = 'auto';

    const combatHeader = document.createElement('div');
    combatHeader.style.cssText =
      'display:flex;justify-content:space-between;align-items:center;flex-shrink:0;border-bottom:1px solid #000;margin:0 -8px 4px;padding:2px 8px;background:#2b1d0c;font-size:10px;font-weight:bold;color:#f3d98b;';
    combatHeader.innerHTML = `<span>⚔️ 전투 로그 (실시간)</span><span style="font-size:9px;color:#94a3b8;">흰색</span>`;
    combat.appendChild(combatHeader);

    const combatBody = document.createElement('div');
    combatBody.id = 'combat-standalone-body';
    combatBody.style.cssText = 'flex:1;min-height:0;overflow-y:auto;';
    combat.appendChild(combatBody);

    parent.appendChild(combat);
    this.combatEl = combat;
    this.combatBodyEl = combatBody;

    this.updateChatTabs();
    this.updateChatChannelTag();
    this.renderChat();
    this.renderCombat();
  }

  private updateChatTabs(): void {
    this.chatHeaderEl?.querySelectorAll<HTMLElement>('[data-tab]').forEach((t) => {
      const on = t.dataset.tab === this.currentChatTab;
      t.style.background = on ? '#1b150e' : 'transparent';
      t.style.color = on ? '#f3d98b' : '#cdb98a';
    });
  }

  private updateChatChannelTag(): void {
    if (!this.chatChannelTagEl) return;
    switch (this.currentChatTab) {
      case 'party':
        this.chatChannelTagEl.textContent = '[파티]';
        this.chatChannelTagEl.style.color = '#f97316'; // 주황
        break;
      case 'guild':
        this.chatChannelTagEl.textContent = '[길드]';
        this.chatChannelTagEl.style.color = '#22c55e'; // 초록
        break;
      case 'broadcast':
        this.chatChannelTagEl.textContent = '[방송]';
        this.chatChannelTagEl.style.color = '#0284c7'; // 진한하늘색
        break;
      case 'world':
        this.chatChannelTagEl.textContent = '[월드]';
        this.chatChannelTagEl.style.color = '#10b981'; // 초록
        break;
      case 'normal':
      case 'aggregate':
      default:
        this.chatChannelTagEl.textContent = '[일반]';
        this.chatChannelTagEl.style.color = '#ffffff'; // 흰색
        break;
    }
  }

  private submitChat(text: string): void {
    if (!this.sendHandler) return;

    if (text.startsWith('/p ') || text.startsWith('/party ')) {
      const msg = text.replace(/^\/(p|party)\s+/, '');
      if (msg) this.sendHandler('party', msg);
    } else if (text.startsWith('/g ') || text.startsWith('/guild ')) {
      const msg = text.replace(/^\/(g|guild)\s+/, '');
      if (msg) this.sendHandler('guild', msg);
    } else if (text.startsWith('/b ') || text.startsWith('/broadcast ')) {
      const msg = text.replace(/^\/(b|broadcast)\s+/, '');
      if (msg) this.sendHandler('broadcast', msg);
    } else if (text.startsWith('/w ') || text.startsWith('/whisper ')) {
      const parts = text.split(/\s+/);
      if (parts.length >= 3) {
        const to = parts[1];
        const msg = parts.slice(2).join(' ');
        this.sendHandler('whisper', msg, to);
      } else {
        this.addMessage({
          id: Math.random().toString(),
          timestamp: Date.now(),
          text: '사용법: /w [상대이름] [메시지]',
          category: 'system',
        });
      }
    } else if (text.startsWith('/world ') || text.startsWith('/s ')) {
      const msg = text.replace(/^\/(world|s)\s+/, '');
      if (msg) this.sendHandler('world', msg);
    } else {
      // 현재 선택된 탭 컨텍스트에 따라 전송 (종합 탭일 경우 기본 'normal' 일반채팅)
      const targetChan = this.currentChatTab === 'aggregate' ? 'normal' : this.currentChatTab;
      this.sendHandler(targetChan, text);
    }
  }

  public addMessage(msg: LogMessage): void {
    this.messages.push(msg);
    if (this.messages.length > this.maxMessages) {
      this.messages.shift();
    }
    const isCombat = CombatLogHUD.COMBAT_CATS.has(msg.category as string);
    if (isCombat) {
      this.renderCombat();
    } else {
      this.renderChat();
    }
  }

  private renderChat(): void {
    if (!this.chatBodyEl) return;

    this.chatBodyEl.innerHTML = '';
    for (const msg of this.messages) {
      const isCombat = CombatLogHUD.COMBAT_CATS.has(msg.category as string);
      if (isCombat) continue; // 전투 메시지는 별도 전투창에 표시

      // 탭 필터링
      if (this.currentChatTab !== 'aggregate') {
        if (this.currentChatTab === 'normal' && msg.category !== 'chat_normal' && msg.category !== 'chat_all') continue;
        if (this.currentChatTab === 'party' && msg.category !== 'chat_party' && msg.category !== 'system') continue;
        if (this.currentChatTab === 'guild' && msg.category !== 'chat_guild') continue;
        if (this.currentChatTab === 'broadcast' && msg.category !== 'chat_broadcast') continue;
        if (this.currentChatTab === 'world' && msg.category !== 'chat_world' && msg.category !== 'system') continue;
      }

      const line = document.createElement('div');
      line.style.marginBottom = '3px';
      line.style.lineHeight = '1.35';

      let color = '#ffffff';
      if (msg.category === 'chat_normal' || msg.category === 'chat_all') {
        color = '#ffffff'; // 일반채팅 흰색
      } else if (msg.category === 'chat_party') {
        color = '#f97316'; // 파티채팅 주황
      } else if (msg.category === 'chat_guild') {
        color = '#22c55e'; // 길드채팅 초록
      } else if (msg.category === 'chat_broadcast') {
        color = '#0284c7'; // 방송채널 진한하늘색
      } else if (msg.category === 'chat_world') {
        color = '#10b981'; // 월드채널 초록
      } else if (msg.category === 'chat_whisper') {
        color = '#f472b6'; // 귓속말 핑크
      } else if (msg.category === 'system') {
        color = '#79c0ff'; // 시스템 안내 하늘색
      } else if (msg.category === 'crafting') {
        color = '#a3e635';
      }

      line.style.color = color;
      line.textContent = msg.text;
      this.chatBodyEl.appendChild(line);
    }

    this.chatBodyEl.scrollTop = this.chatBodyEl.scrollHeight;
  }

  private renderCombat(): void {
    if (!this.combatBodyEl) return;

    this.combatBodyEl.innerHTML = '';
    for (const msg of this.messages) {
      const isCombat = CombatLogHUD.COMBAT_CATS.has(msg.category as string);
      if (!isCombat) continue; // 전투창에는 전투 로그만 출력

      const line = document.createElement('div');
      line.style.marginBottom = '2px';
      line.style.lineHeight = '1.3';
      // 전투 로그는 요청에 따라 흰색 계열로 깔끔하게 표시
      line.style.color = '#ffffff';
      line.textContent = msg.text;
      this.combatBodyEl.appendChild(line);
    }

    this.combatBodyEl.scrollTop = this.combatBodyEl.scrollHeight;
  }
}
